import { ID, Response } from 'app/components/Table/core/types';

import { exportCSV } from 'app/modules/reporting/core/helpers';
import { SensorType } from 'app/modules/system-settings/devices/core/enum';

import { trimObject } from 'app/helpers';
import { getResponseError } from 'app/helpers/errors';
import { downloadZip } from 'app/helpers/utils';

import { API_URL } from 'app/constants/environments';

import {
  BinAsset,
  Device,
  DeviceRequest,
  DevicesQueryResponse,
  Sensor,
  SensorRequest,
  SensorsQueryResponse,
} from './type';
import axios, { AxiosResponse } from 'axios';
import qs from 'qs';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

const DEVICE_URL = `${API_URL}/api`;
const SENSOR_URL = `${API_URL}/api`;

const getDevices = (query: string): Promise<DevicesQueryResponse> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios.get(`${DEVICE_URL}/orgs/${organizationId}/devices?${query}`).then(
    (
      d: AxiosResponse<{
        data: {
          items: Array<Device>;
          total: number;
        };
      }>,
    ) => {
      return {
        data: d.data.data,
      };
    },
  );
};

const createDevice = (request: DeviceRequest): Promise<Device | undefined> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .post(`${DEVICE_URL}/orgs/${organizationId}/devices`, trimObject(request))
    .then((response: AxiosResponse<Response<Device>>) => response.data)
    .then((response: Response<Device>) => response.data);
};

const updateDevice = (idDevice: ID, request: DeviceRequest): Promise<Device | undefined> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .patch(`${DEVICE_URL}/orgs/${organizationId}/devices/${idDevice}`, trimObject(request))
    .then((response: AxiosResponse<Response<Device>>) => response.data)
    .then((response: Response<Device>) => response.data);
};

const deleteDevice = async (idDevice: ID): Promise<void> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    await axios.delete(`${DEVICE_URL}/orgs/${organizationId}/devices/${idDevice}`);
  } catch (error) {
    console.log(`Error deleting device ID ${idDevice}`);
    throw error;
  }
};

const generateCodeDevice = (idDevice: ID): Promise<Device | undefined> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .patch(`${DEVICE_URL}/orgs/${organizationId}/devices/${idDevice}/generate`)
    .then((response: AxiosResponse<Response<Device>>) => response.data)
    .then((response: Response<Device>) => response.data);
};

const getExpirySensors = async (query: string): Promise<any> => {
  try {
    const organizationId = useOrganizationStore.getState().state.organization?.id;

    const response = await axios.get<{ success: boolean; data: any[] }>(
      `${SENSOR_URL}/orgs/${organizationId}/thing?${query}`,
    );

    if (!response.data.success) {
      throw new Error('Failed to get expiry sensors');
    }

    return {
      data: response.data.data,
    };
  } catch (error) {
    getResponseError(error);
    throw error;
  }
};

const getSensors = (query: string): Promise<SensorsQueryResponse> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .get(`${SENSOR_URL}/orgs/${organizationId}/sensors?${query}`)
    .then((d: AxiosResponse<SensorsQueryResponse>) => {
      return {
        data: d.data.data,
      };
    })
    .catch((error) => {
      throw error;
    });
};

const createSensor = (request: SensorRequest): Promise<Sensor | undefined> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .post(`${SENSOR_URL}/orgs/${organizationId}/sensors`, trimObject(request))
    .then((response: AxiosResponse<Response<Sensor>>) => response.data)
    .then((response: Response<Sensor>) => response.data);
};

const updateSensor = (idSensor: ID, request: SensorRequest): Promise<Sensor | undefined> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .patch(`${SENSOR_URL}/orgs/${organizationId}/sensors/${idSensor}`, trimObject(request))
    .then((response: AxiosResponse<Response<Sensor>>) => response.data)
    .then((response: Response<Sensor>) => response.data);
};

const deleteSensor = async (idSensor: ID, type: SensorType): Promise<void> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    await axios.delete(`${SENSOR_URL}/orgs/${organizationId}/sensors/${idSensor}/type/${type}`);
  } catch (error) {
    console.log(`Error deleting sensor ID ${idSensor}`);
    throw error;
  }
};

const getBinAssets = async (query: string, idMac?: string) => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios.get(`${API_URL}/api/orgs/${organizationId}/bins/${idMac}/assets?${query}`).then(
    (
      d: AxiosResponse<{
        data: {
          items: Array<BinAsset>;
          total: number;
        };
      }>,
    ) => {
      return {
        data: d.data.data,
      };
    },
  );
};

const downloadZipAssets = async (idMAC: string, ids: string[]) => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    const response = await axios.get(
      `${API_URL}/api/orgs/${organizationId}/bins/${idMAC}/assets/download`,
      {
        params: {
          ids,
        },
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
        responseType: 'blob',
      },
    );

    downloadZip(response.data);

    return true;
  } catch (error) {
    console.error('Error downloading zip file:', error);
    throw error;
  }
};

const downloadHeartbeat = async (params?: Record<string, ID>): Promise<void> => {
  try {
    const response: AxiosResponse<{ data: string }> = await axios.get(
      `${API_URL}/api/report/heartbeat`,
      { params },
    );

    if (response.data) exportCSV(response.data.data, 'Heartbeat');
  } catch (error) {
    console.error('Error downloading heartbeat:', error);
    getResponseError(error);
    throw error;
  }
};

const downloadTrasaction = async (params: Record<string, ID>) => {
  try {
    const response: AxiosResponse<{ data: string }> = await axios.get(
      `${API_URL}/api/report/transaction`,
      {
        params,
      },
    );
    if (response.data) exportCSV(response.data.data, 'MQTT');
  } catch (error) {
    console.error('Error downloading transaction:', error);
    getResponseError(error);
    throw error;
  }
};

const downloadTrasactionLedger = async (params: Record<string, ID>) => {
  try {
    const response: AxiosResponse<{ data: string }> = await axios.get(
      `${API_URL}/api/report/transaction-ledger`,
      {
        params,
      },
    );
    if (response.data) exportCSV(response.data.data, 'Transaction-Ledger');
  } catch (error) {
    console.error('Error downloading transaction ledger:', error);
    getResponseError(error);
    throw error;
  }
};

const downloadExpirySensor = async (params?: Record<string, ID>): Promise<void> => {
  try {
    const response: AxiosResponse<{ data: string }> = await axios.get(
      `${API_URL}/api/report/expiry`,
      { params },
    );

    if (response.data) exportCSV(response.data.data, 'Heartbeat');
  } catch (error) {
    getResponseError(error);
    throw error;
  }
};

const downloadTabletActivityLog = async (params: Record<string, ID>): Promise<void> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    const response: AxiosResponse<{ data: string }> = await axios.get(
      `${API_URL}/api/report/orgs/${organizationId}/tablet-user-login`,
      { params },
    );

    if (response.data) exportCSV(response.data.data, 'Tablet activity log');
  } catch (error) {
    getResponseError(error);
    throw error;
  }
};

export {
  createDevice,
  createSensor,
  deleteDevice,
  deleteSensor,
  downloadHeartbeat,
  downloadTrasaction,
  downloadTrasactionLedger,
  downloadZipAssets,
  downloadExpirySensor,
  downloadTabletActivityLog,
  generateCodeDevice,
  getBinAssets,
  getDevices,
  getSensors,
  getExpirySensors,
  updateDevice,
  updateSensor,
};
